import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Tooltip,
  Chip,
  Paper,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  InsertDriveFile as FileIcon,
  Code as CodeIcon,
  Description as DocumentIcon,
  Image as ImageIcon,
  Settings as ConfigIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

const FileExplorer = ({ 
  workspace = {}, 
  onFileSelect, 
  selectedFile,
  isLoading = false,
  onRefresh 
}) => {
  const [expanded, setExpanded] = useState(['src', 'tests', 'docs']);
  const [fileStructure, setFileStructure] = useState({});
  
  // Process workspace structure on mount and when workspace changes
  useEffect(() => {
    if (workspace.structure) {
      setFileStructure(workspace.structure);
    } else if (workspace.files) {
      // Build structure from flat file list
      const structure = {};
      
      Object.keys(workspace.files).forEach(filePath => {
        const parts = filePath.split('/');
        let current = structure;
        
        parts.forEach((part, index) => {
          if (index === parts.length - 1) {
            // This is a file
            current[part] = {
              type: 'file',
              path: filePath,
              size: workspace.files[filePath].size || 0
            };
          } else {
            // This is a directory
            if (!current[part]) {
              current[part] = { type: 'directory' };
            }
            current = current[part];
          }
        });
      });
      
      setFileStructure(structure);
    }
  }, [workspace]);

  // Handle node expansion
  const handleToggle = (nodeId) => {
    setExpanded(prev => {
      const newExpanded = [...prev];
      const index = newExpanded.indexOf(nodeId);
      
      if (index === -1) {
        newExpanded.push(nodeId);
      } else {
        newExpanded.splice(index, 1);
      }
      
      return newExpanded;
    });
  };

  // Handle file selection
  const handleFileSelect = (filePath) => {
    if (onFileSelect) {
      onFileSelect(filePath);
    }
  };

  // Get file icon based on extension
  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    switch (extension) {
      case 'js':
      case 'jsx':
      case 'ts':
      case 'tsx':
      case 'py':
      case 'java':
      case 'cpp':
      case 'c':
      case 'go':
      case 'rs':
        return <CodeIcon fontSize="small" sx={{ color: 'success.main' }} />;
      case 'md':
      case 'txt':
      case 'doc':
      case 'docx':
        return <DocumentIcon fontSize="small" sx={{ color: 'info.main' }} />;
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'svg':
        return <ImageIcon fontSize="small" sx={{ color: 'warning.main' }} />;
      case 'json':
      case 'yml':
      case 'yaml':
      case 'toml':
      case 'ini':
      case 'config':
        return <ConfigIcon fontSize="small" sx={{ color: 'secondary.main' }} />;
      default:
        return <FileIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };
  
  // Render tree items recursively using List components
  const renderTreeItems = (structure, path = '', depth = 0) => {
    return Object.keys(structure).sort().map(key => {
      const item = structure[key];
      const currentPath = path ? `${path}/${key}` : key;
      const isDirectory = item.type === 'directory';
      const isExpanded = expanded.includes(currentPath);
      const isSelected = selectedFile === currentPath;
      
      if (isDirectory) {
        // Directory node
        const childKeys = Object.keys(item).filter(k => k !== 'type');
        const hasChildren = childKeys.length > 0;
        
        return (
          <React.Fragment key={currentPath}>
            <ListItem disablePadding sx={{ pl: depth * 2 }}>
              <ListItemButton
                onClick={() => handleToggle(currentPath)}
                selected={false}
                sx={{
                  minHeight: 32,
                  py: 0.5,
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {hasChildren ? (
                    isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />
                  ) : null}
                </ListItemIcon>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {isExpanded ? (
                    <FolderOpenIcon fontSize="small" sx={{ color: 'primary.main' }} />
                  ) : (
                    <FolderIcon fontSize="small" sx={{ color: 'primary.main' }} />
                  )}
                </ListItemIcon>
                <ListItemText 
                  primary={key} 
                  primaryTypographyProps={{ 
                    variant: 'body2',
                    sx: { fontSize: '0.875rem' }
                  }}
                />
                {hasChildren && (
                  <Chip 
                    size="small" 
                    label={childKeys.length} 
                    sx={{ height: 18, fontSize: '0.7rem' }}
                  />
                )}
              </ListItemButton>
            </ListItem>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {hasChildren && renderTreeItems(item, currentPath, depth + 1)}
              </List>
            </Collapse>
          </React.Fragment>
        );
      } else {
        // File node
        const fileData = workspace.files?.[currentPath];
        
        return (
          <ListItem key={currentPath} disablePadding sx={{ pl: depth * 2 }}>
            <ListItemButton
              onClick={() => handleFileSelect(currentPath)}
              selected={isSelected}
              sx={{
                minHeight: 32,
                py: 0.5,
                backgroundColor: isSelected ? 'action.selected' : 'transparent',
                '&:hover': {
                  backgroundColor: isSelected ? 'action.selected' : 'action.hover'
                },
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.selected'
                  }
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }} />
              <ListItemIcon sx={{ minWidth: 32 }}>
                {getFileIcon(key)}
              </ListItemIcon>
              <ListItemText 
                primary={key}
                secondary={fileData?.size ? formatFileSize(fileData.size) : null}
                primaryTypographyProps={{ 
                  variant: 'body2',
                  sx: { 
                    fontSize: '0.875rem',
                    fontWeight: isSelected ? 'medium' : 'normal'
                  }
                }}
                secondaryTypographyProps={{
                  variant: 'caption',
                  sx: { fontSize: '0.7rem' }
                }}
              />
            </ListItemButton>
          </ListItem>
        );
      }
    });
  };

  return (
    <Paper 
      elevation={1} 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        borderBottom: 1, 
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'medium' }}>
          Project Files
        </Typography>
        <Tooltip title="Refresh workspace">
          <IconButton 
            size="small" 
            onClick={onRefresh}
            disabled={isLoading}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* File tree */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : Object.keys(fileStructure).length === 0 ? (
          <Alert severity="info" sx={{ m: 2 }}>
            No files generated yet. Start by executing some development tasks.
          </Alert>
        ) : (
          <List dense sx={{ py: 0 }}>
            {renderTreeItems(fileStructure)}
          </List>
        )}
      </Box>

      {/* Footer with workspace path */}
      {workspace.workspace_path && (
        <Box sx={{ 
          p: 1, 
          borderTop: 1, 
          borderColor: 'divider',
          backgroundColor: 'background.default'
        }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'text.secondary',
              fontSize: '0.7rem',
              fontFamily: 'monospace'
            }}
          >
            {workspace.workspace_path}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default FileExplorer;